import { Injectable } from '@angular/core';
import { HttpService } from './http.service';
import { UserPaymentMethods, CardPaymentMethod, UserCard, Balance, UserAdministratedBalances, BalanceWithKey } from '../interfaces';
import { isEdenredRechargeActive, removeUnavailableEdenredMethods } from './edenred-availability';
import { environment } from 'src/environments/environment';

/**
 * Centralized service for managing payment methods data and business logic.
 * Handles both client-accepted payment methods and user payment methods,
 * with caching to reduce redundant API calls.
 */
@Injectable({
  providedIn: 'root'
})
export class PaymentMethodsService {

  public PREPAID_BINS: string[] = [
    "450817", // Pomelo
    "483031", // Mach (Prepago BCI)
    "412002", // Mach (Prepago BCI)
    "412003", // Mach (Prepago BCI)
    "494615", // Prepago banco de chile
    "234061", // Chek
    "525384", // Chek
    "521253", // Chek
    "553452", // Chek
    "559391", // Prepago Copec
    "524101", // Dale (Prepago Coopeuch)
    "531926", // Dale (Prepago Coopeuch)
    "542149", // Prepago Fintual
    "519858", // Prepago Global66
    "555505", // Prepago Global66
    "530478", // Prepago Hub Site
    "533514", // Prepago Hub Site
    "405188", // Prepago INTL HDQTRS-CENTER OWNED
    "410027", // Prepago La Polar
    "230863", // Tapp (Prepago Los Andes)
    "522416", // Tapp (Prepago Los Andes)
    "542455", // Tapp (Prepago Los Andes)
    "510869", // Mercadopago
    "534916", // Prex
    "516998", // Prepago Banco Scotiabank
    "527698", // Prepago Banco Scotiabank
    "555912", // Prepago Los Heroes
    "558910", // Prepago Los Heroes
    "559787", // Prepago Los Heroes
    "559793", // Prepago Los Heroes
    "230873", // Prepago Los Heroes
    "533941", // Prepago SumUp
    "555834", // Prepago Banco Santander (Superdigital)
    "538759", // Prepago Banco Santander
    "538765", // Prepago Banco Santander
    "494614", // Prepago Banco Santander
    "517608", // Tenpo
    "541613", // Tenpo
    "423484", // Prepago Tricard (Tricot)
    "233031", // U-Pay Processing
    "233035", // U-Pay Processing
    "518286", // U-Pay Processing
    "523250" // U-Pay Processing
  ]
  /** Cache for client accepted payment methods, keyed by clientId */
  private clientMethodsCache: Map<string, { [key: string]: boolean }> = new Map();

  /** Cache for user payment methods */
  private userMethodsCache: UserPaymentMethods | null = null;

  /** Cache for user payment methods by store, keyed by storeId */
  private userMethodsByStoreCache: Map<string, UserPaymentMethods> = new Map();

  constructor(private http: HttpService) {}

  // ==================== Data Retrieval Methods ====================

  /**
   * Fetches client accepted payment methods with caching.
   * @param forceRefresh - If true, bypasses cache and fetches fresh data
   * @returns Promise with payment methods object or empty object on error
   */
  async getClientAcceptedMethods(forceRefresh: boolean = false): Promise<{ [key: string]: boolean }> {
    if (!forceRefresh && this.clientMethodsCache.has(environment.clientId)) {
      return this.clientMethodsCache.get(environment.clientId)!;
    }

    try {
      const response = await this.http.getClientPaymentMethods(environment.clientId);
      if (response.successful) {
        this.clientMethodsCache.set(environment.clientId, response.data);
        return response.data;
      }
      return {};
    } catch (error) {
      console.error('Error fetching client payment methods:', error);
      return {};
    }
  }

  /**
   * Fetches user payment methods with caching.
   * @param forceRefresh - If true, bypasses cache and fetches fresh data
   * @returns Promise with user payment methods or empty object on error
   */
  async getUserPaymentMethods(forceRefresh: boolean = false): Promise<UserPaymentMethods> {
    if (!forceRefresh && this.userMethodsCache) {
      return this.userMethodsCache;
    }

    try {
      const response = await this.http.getUserPaymentMethods();
      if (response.successful) {
        this.userMethodsCache = response.data;
        return response.data;
      }
      return {};
    } catch (error) {
      console.error('Error fetching user payment methods:', error);
      return {};
    }
  }

  /**
   * Fetches user payment methods for a specific store with caching.
   * @param storeId - The store ID to fetch payment methods for
   * @param forceRefresh - If true, bypasses cache and fetches fresh data
   * @returns Promise with user payment methods or empty object on error
   */
  async getUserPaymentMethodsForStore(
    storeId: string,
    forceRefresh: boolean = false,
    allowNonFloatBalances: boolean = false,
  ): Promise<UserPaymentMethods> {
    if (!forceRefresh && this.userMethodsByStoreCache.has(storeId)) {
      return this.userMethodsByStoreCache.get(storeId)!;
    }

    try {
      const response = await this.http.getUserPaymentMethodsForStore(storeId, allowNonFloatBalances);
      if (response.successful) {
        this.userMethodsByStoreCache.set(storeId, response.data);
        return response.data;
      }
      return {};
    } catch (error) {
      console.error('Error fetching user payment methods for store:', error);
      return {};
    }
  }

  // ==================== Business Logic Methods ====================

  balanceIsPositive(balanceAmount: number | {[productGroup: string]: number}): boolean {
    if (typeof balanceAmount === 'number') {
      return balanceAmount > 0;
    }
    // If balance is an object (non-float), consider it positive if it has any active entry with positive amount
    return Object.values(balanceAmount).some(
      (entry: number) => entry > 0
    );
  }

  addBalanceToTotal(total: number, balanceAmount: number | {[productGroup: string]: number}): number {
    if (typeof balanceAmount === 'number') {
      return total + balanceAmount;
    }
    // If balance is an object (non-float), sum all active entries with positive amounts
    const balanceTotal = Object.values(balanceAmount).reduce(
      (sum: number, entry: number) => {
        if (entry > 0) {
          return sum + entry;
        }
        return sum;
      },
      0
    );
    return total + balanceTotal;
  }

  /**
   * Checks if the user has any active balance with funds using cached data.
   * @returns true if user has at least one active balance with positive funds
   */
  hasActiveBalance(): boolean {
    if (!this.userMethodsCache?.balance) {
      return false;
    }

    return Object.values(this.userMethodsCache.balance).some(
      (balance: Balance) => balance.active && this.balanceIsPositive(balance.balance)
    );
  }

  /**
   * Checks if user has any card that matches an accepted provider using cached data.
   * For MercadoPago cards, also validates marketplace ID.
   * @param marketplaceId - Optional marketplace ID for MercadoPago validation
   * @returns true if user has at least one accepted card
   */
  hasAcceptedCard(marketplaceId: string = environment.marketplaceId): boolean {
    const clientMethods = this.clientMethodsCache.get(environment.clientId) ?? {};

    if (!this.userMethodsCache?.cards || this.userMethodsCache.cards.length === 0) {
      return false;
    }

    return this.userMethodsCache.cards.some((card: UserCard) => {
      // Check if the card provider is accepted by the client
      if (!clientMethods[card.provider]) {
        return false;
      }

      // For MercadoPago cards, validate marketplace ID
      if (card.provider === CardPaymentMethod.MERCADOPAGO) {
        if (card.providerSpecificData?.marketplaceId) {
          return card.providerSpecificData.marketplaceId === marketplaceId;
        }
      }

      return true;
    });
  }

  /**
   * Checks if user has any valid payment method (active balance or accepted card) using cached data.
   * @param marketplaceId - Optional marketplace ID for MercadoPago validation
   * @returns true if user has at least one valid payment method
   */
  hasValidPaymentMethod(marketplaceId: string = environment.marketplaceId): boolean {
    return this.hasActiveBalance() || this.hasAcceptedCard(marketplaceId);
  }

  /**
   * Determines the inscription route the user should be redirected to.
   * Uses cached client methods. Prioritizes Klap over MercadoPago.
   * @param storeId - Store ID to include in the route
   * @returns The inscription route path or null if no redirect needed
   */
  getRequiredInscriptionRoute(storeId: string): string | null {
    const clientMethods = this.clientMethodsCache.get(environment.clientId) ?? {};

    // Prioritize Klap inscription
    if (clientMethods[CardPaymentMethod.KLAP]) {
      return `/klap-card-inscription/${storeId}`;
    }

    // Fallback to MercadoPago inscription
    if (clientMethods[CardPaymentMethod.MERCADOPAGO]) {
      return `/mp-card-inscription/${storeId}`;
    }

    // No redirect needed if neither Klap nor MercadoPago is accepted
    return null;
  }

  /**
   * Gets the set of enabled card providers using cached client methods.
   * @returns Set of enabled CardPaymentMethod values
   */
  getEnabledCardProviders(): Set<CardPaymentMethod> {
    const clientMethods = this.clientMethodsCache.get(environment.clientId) ?? {};
    const enabledProviders = new Set<CardPaymentMethod>();

    Object.values(CardPaymentMethod).forEach(provider => {
      if (clientMethods[provider]) {
        enabledProviders.add(provider);
      }
    });

    return enabledProviders;
  }

  /**
   * Gets cards for a specific provider using cached user payment methods.
   * @param provider - The card payment method provider
   * @param marketplaceId - Optional marketplace ID for MercadoPago filtering
   * @returns Array of UserCard objects for the specified provider
   */
  getCardsForProvider(
    provider: CardPaymentMethod,
    marketplaceId: string = environment.marketplaceId
  ): UserCard[] {
    if (!this.userMethodsCache?.cards) {
      return [];
    }

    return this.userMethodsCache.cards.filter(card => {
      if (card.provider !== provider) {
        return false;
      }

      // For MercadoPago cards, validate marketplace ID
      if (card.provider === CardPaymentMethod.MERCADOPAGO && card.providerSpecificData?.marketplaceId) {
        return card.providerSpecificData.marketplaceId === marketplaceId;
      }

      return true;
    });
  }

  /**
   * Checks if a provider has any registered cards using cached data.
   * @param provider - The card payment method provider
   * @param marketplaceId - Optional marketplace ID for MercadoPago filtering
   * @returns true if the provider has at least one card
   */
  hasCardsForProvider(
    provider: CardPaymentMethod,
    marketplaceId: string = environment.marketplaceId
  ): boolean {
    return this.getCardsForProvider(provider, marketplaceId).length > 0;
  }

  /**
   * Checks if user has any registered cards across all enabled providers using cached data.
   * @param marketplaceId - Optional marketplace ID for MercadoPago filtering
   * @returns true if user has at least one card
   */
  hasAnyCards(marketplaceId: string = environment.marketplaceId): boolean {
    const enabledProviders = this.getEnabledCardProviders();
    return Array.from(enabledProviders).some(
      provider => this.hasCardsForProvider(provider, marketplaceId)
    );
  }

  /**
   * Gets the display name for a card provider.
   * @param provider - The card payment method
   * @returns Human-readable provider name
   */
  getProviderDisplayName(provider: CardPaymentMethod): string {
    const displayNames: Record<CardPaymentMethod, string> = {
      [CardPaymentMethod.TRANSBANK]: 'Transbank',
      [CardPaymentMethod.MERCADOPAGO]: 'Mercado Pago',
      [CardPaymentMethod.KLAP]: 'Klap',
      [CardPaymentMethod.DEMO]: 'Demo',
    };
    return displayNames[provider] || provider;
  }

  /**
   * Gets available balance recharge methods using cached client methods.
   * @returns Array of available rechargeable balance methods
   */
  getAvailableRechargeMethods(): UserAdministratedBalances[] {
    const clientMethods = this.clientMethodsCache.get(environment.clientId) ?? {};
    const methods = Object.keys(clientMethods)
      .filter(key => clientMethods[key] && Object.values(UserAdministratedBalances).includes(key as UserAdministratedBalances))
      .map(key => key as UserAdministratedBalances);
    return removeUnavailableEdenredMethods(methods);
  }

  /**
   * Calculates the total balance across all user balances.
   * Only sums active balances with positive amounts.
   * @returns Total balance amount
   */
  getTotalBalance(): number {
    if (!this.userMethodsCache?.balance) {
      return 0;
    }

    return Object.values(this.userMethodsCache.balance).reduce(
      (total, balance: Balance) => {
        if (balance.active && this.balanceIsPositive(balance.balance)) {
          return this.addBalanceToTotal(total, balance.balance);
        }
        return total;
      },
      0
    );
  }

  /**
   * Calculates the total balance for a specific store.
   * Only sums active balances with positive amounts.
   * @param storeId - The store ID to calculate balance for
   * @returns Total balance amount for the store
   */
  getTotalBalanceForStore(storeId: string): number {
    const storePaymentMethods = this.userMethodsByStoreCache.get(storeId);
    if (!storePaymentMethods?.balance) {
      return 0;
    }

    return Object.values(storePaymentMethods.balance).reduce(
      (total, balance: Balance) => {
        if (balance.active && balance.inSchedule && this.balanceIsPositive(balance.balance)) {
          return this.addBalanceToTotal(total, balance.balance);
        }
        return total;
      },
      0
    );
  }

  /**
   * Checks if user has valid payment methods for a specific store.
   * Valid payment methods include:
   * - Transbank cards
   * - MercadoPago cards
   * - Klap cards
   * - Balance that is NOT user-administered (not in UserAdministratedBalances enum)
   * @param storeId - The store ID to check payment methods for
   * @returns true if user has at least one valid payment method for the store
   */
  hasValidPaymentMethodForStore(storeId: string): boolean {
    const storePaymentMethods = this.userMethodsByStoreCache.get(storeId);
    if (!storePaymentMethods) {
      return false;
    }

    // Check for normalized cards format
    if (storePaymentMethods.cards && storePaymentMethods.cards.length > 0) {
      return true;
    }

    // Check for non-user-administered balances
    if (storePaymentMethods.balance) {
      const balanceIds = Object.keys(storePaymentMethods.balance);
      const hasNonAdminBalance = balanceIds.some(balanceId =>
        !Object.values(UserAdministratedBalances).includes(balanceId as UserAdministratedBalances)
      );
      if (hasNonAdminBalance) {
        return true;
      }
    }

    return false;
  }

  /**
   * Gets the store accepted payment methods as a simple object.
   * @param storeAcceptedPaymentMethods - Store's accepted payment methods from Store interface
   * @returns Normalized object with payment method keys set to true
   */
  getStoreAcceptedPaymentMethodsMap(storeAcceptedPaymentMethods: {[key: string]: any}): {[key: string]: boolean} {
    const map: {[key: string]: boolean} = {};
    Object.keys(storeAcceptedPaymentMethods).forEach((key: string) => {
      map[key] = true;
    });
    return map;
  }

  // ==================== Balance Methods ====================

  /**
   * Gets all balances from cached user payment methods.
   * @returns Array of balance entries with their keys
   */
  getBalances(): BalanceWithKey[] {
    if (!this.userMethodsCache?.balance) {
      return [];
    }
    return Object.entries(this.userMethodsCache.balance).map(([key, balance]) => ({
      key,
      balance
    }));
  }

  /**
   * Gets only active balances with positive funds from cached data.
   * @returns Array of active balance entries
   */
  getActiveBalances(): BalanceWithKey[] {
    return this.getBalances().filter(
      ({ balance }) => balance.active && this.balanceIsPositive(balance.balance)
    );
  }

  /**
   * Checks if user has any balance entries using cached data.
   * @returns true if user has at least one balance
   */
  hasAnyBalance(): boolean {
    return this.getBalances().length > 0;
  }

  /**
   * Gets the display name for a balance type.
   * @param balanceKey - The balance key (e.g., 'amipass', 'edenred')
   * @returns Human-readable balance name
   */
  getBalanceDisplayName(balanceKey: string): string {
    const displayNames: Record<string, string> = {
      'amipass': 'Amipass',
      // Ticket Restaurant only needs telling apart from Junaeb once both are offered. Until
      // the Edenred flow is enabled, the single balance keeps the name users already know.
      'edenred': isEdenredRechargeActive() ? 'Edenred Ticket Restaurant' : 'Edenred',
      'edenred_junaeb': 'Edenred Junaeb',
      'pluxee': 'Pluxee',
    };
    return displayNames[balanceKey] || balanceKey;
  }

  // ==================== Cache State Methods ====================

  /**
   * Checks if user payment methods are cached.
   * @returns true if user methods cache is populated
   */
  isUserPaymentMethodsCached(): boolean {
    return this.userMethodsCache !== null;
  }

  /**
   * Checks if client payment methods are cached.
   * @returns true if client methods cache is populated for current client
   */
  isClientMethodsCached(): boolean {
    return this.clientMethodsCache.has(environment.clientId);
  }

  // ==================== Cache Management ====================

  /**
   * Clears all cached data.
   * Useful when user logs out or payment methods change.
   */
  clearCache(): void {
    this.clientMethodsCache.clear();
    this.userMethodsCache = null;
    this.userMethodsByStoreCache.clear();
  }

  /**
   * Clears only user-related cache.
   * Useful when user payment methods are updated.
   */
  clearUserCache(): void {
    this.userMethodsCache = null;
    this.userMethodsByStoreCache.clear();
  }

  isPrepaidCard(cardBin: string): boolean {
    return this.PREPAID_BINS.includes(cardBin);
  }
}
